import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Ticket, Star, Loader2, Globe, Film, Play, Flag, MessageSquare, Send, X, Crown, Users, Languages } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";

// Browser lang → 2-letter code we may pass to /copy
const BROWSER_LANG_MAP = {
  es: "es", fr: "fr", de: "de", it: "it", pt: "pt", ja: "ja", ko: "ko",
  zh: "zh", hi: "hi", ar: "ar", ru: "ru", tr: "tr", nl: "nl", sv: "sv", pl: "pl", en: "en",
};
const _detectBrowserLang = () => {
  try {
    const raw = (navigator.language || navigator.userLanguage || "en").toLowerCase().slice(0, 2);
    return BROWSER_LANG_MAP[raw] || "en";
  } catch { return "en"; }
};

export default function FilmDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [film, setFilm] = useState(null);
  const [error, setError] = useState(null);
  const [buying, setBuying] = useState(false);
  const [voting, setVoting] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("inappropriate");
  const [reportNote, setReportNote] = useState("");
  const [reporting, setReporting] = useState(false);
  const polledRef = useRef(false);

  // Auto-translated copy state (title + synopsis)
  const browserLang = useMemo(() => _detectBrowserLang(), []);
  const [copy, setCopy] = useState(null); // { title, synopsis, translated, source_lang, target } or null
  const [copyBusy, setCopyBusy] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const copyTriedRef = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/festival/submissions/${id}`);
      setFilm(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Film not found");
    }
  };
  useEffect(() => { load(); }, [id]);

  // Auto-translate title + synopsis based on browser language.
  // Only trigger if browser lang differs from the film's source language
  // AND the source is English (or unset). We never re-translate an already
  // localized film. Falls back silently on any error. Fires at most once per
  // (submission_id, browserLang) so state refreshes (voting, ticket poll) don't
  // hammer the endpoint.
  useEffect(() => {
    if (!film || copyTriedRef.current) return;
    const src = (film.language || "en").toLowerCase();
    if (browserLang === "en" || browserLang === src) { copyTriedRef.current = true; return; }
    copyTriedRef.current = true;
    setCopyBusy(true);
    api.get(`/festival/submissions/${film.submission_id}/copy`, { params: { lang: browserLang } })
      .then((r) => {
        if (r.data?.translated) setCopy({ ...r.data, target: browserLang });
      })
      .catch(() => { /* silent — original still renders */ })
      .finally(() => setCopyBusy(false));
  }, [film, browserLang]);

  const displayTitle = copy && !showOriginal ? copy.title : (film?.title || "");
  const displaySynopsis = copy && !showOriginal ? copy.synopsis : (film?.synopsis || "No synopsis.");

  // Stripe success polling
  useEffect(() => {
    const sid = sp.get("ticket_session_id");
    if (!sid || polledRef.current) return;
    polledRef.current = true;
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/festival/tickets/status/${sid}`);
        if (data.status === "paid") {
          toast.success("Ticket confirmed — enjoy the film!");
          sp.delete("ticket_session_id"); setSp(sp, { replace: true });
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired");
          sp.delete("ticket_session_id"); setSp(sp, { replace: true });
          return;
        }
      } catch (e) {
        console.warn("[filmDetail] festival ticket poll failed", e?.response?.status);
      }
      if (attempts < 10) setTimeout(tick, 2000);
    };
    tick();
  }, []);

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/festival/tickets/checkout", {
        submission_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBuying(false);
    }
  };

  const castVote = async (rating) => {
    setVoting(true);
    try {
      await api.post(`/festival/submissions/${id}/vote`, { rating });
      toast.success(film?.user_status?.has_voted ? "Vote updated" : "Thanks for voting!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    } finally { setVoting(false); }
  };

  const submitReport = async () => {
    if (!user) { toast.error("Sign in to report"); return; }
    setReporting(true);
    try {
      const { data } = await api.post(`/festival/submissions/${id}/report`, {
        reason: reportReason, note: reportNote.trim() || undefined,
      });
      if (data.duplicate) toast.info("You already reported this film. Thanks.");
      else toast.success(data.auto_flagged ? "Reported — film sent to moderation" : "Reported. Thanks for keeping the festival safe.");
      setReportOpen(false); setReportNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally { setReporting(false); }
  };

  // ---- Live Q&A chat ----
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatViewers, setChatViewers] = useState(0);
  const [chatRole, setChatRole] = useState(null); // "admin" | "filmmaker" | "collaborator" | "ticket_holder"
  const [chatStatus, setChatStatus] = useState("idle"); // idle | connecting | live | denied | error
  const [chatUnread, setChatUnread] = useState(0);
  const chatOpenRef = useRef(false);
  const chatBodyRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => { chatOpenRef.current = chatOpen; if (chatOpen) setChatUnread(0); }, [chatOpen]);
  useEffect(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [chatMessages.length, chatOpen]);

  const canJoinChat = !!(
    film?.user_status?.is_filmmaker ||
    film?.user_status?.has_ticket ||
    film?.user_status?.my_role ||
    user?.is_admin
  );

  useEffect(() => {
    // Only connect when the drawer is opened and the user has access
    if (!chatOpen || !canJoinChat) return;
    const tok = localStorage.getItem("cc_token");
    if (!tok) return;
    setChatStatus("connecting");
    const wsUrl = `${API_BASE.replace(/^http/, "ws")}/festival/ws/${id}?token=${encodeURIComponent(tok)}`;
    let ws;
    try { ws = new WebSocket(wsUrl); } catch { setChatStatus("error"); return; }
    wsRef.current = ws;
    ws.onopen = () => setChatStatus("live");
    ws.onmessage = (ev) => {
      let m; try { m = JSON.parse(ev.data); } catch { return; }
      if (m.type === "hello") {
        setChatMessages(m.chat_history || []);
        setChatViewers(m.viewers || 0);
        setChatRole(m.role || null);
      } else if (m.type === "presence") {
        setChatViewers(m.viewers || 0);
      } else if (m.type === "chat") {
        setChatMessages((prev) => [...prev, m].slice(-200));
        if (!chatOpenRef.current) setChatUnread((u) => Math.min(99, u + 1));
      }
    };
    ws.onclose = (ev) => {
      wsRef.current = null;
      if (ev.code === 4403) setChatStatus("denied");
      else if (ev.code === 4401 || ev.code === 4404) setChatStatus("error");
      else setChatStatus("idle");
    };
    ws.onerror = () => { /* swallow — onclose handles state */ };
    return () => { try { ws.close(); } catch {} wsRef.current = null; };
  }, [chatOpen, id, canJoinChat]);

  const sendChat = () => {
    const text = chatInput.trim();
    if (!text) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) { toast.error("Chat disconnected — reopen to retry"); return; }
    ws.send(JSON.stringify({ type: "chat", text }));
    setChatInput("");
  };

  const roleBadge = (role) => {
    if (role === "filmmaker") return { label: "Filmmaker", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40", icon: Crown };
    if (role === "collaborator") return { label: "Crew", cls: "bg-purple-500/20 text-purple-300 border-purple-500/40", icon: Film };
    if (role === "admin") return { label: "Moderator", cls: "bg-sky-500/20 text-sky-300 border-sky-500/40", icon: Crown };
    return null;
  };

  if (error) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-32 px-6 max-w-md mx-auto text-center">
        <Film className="w-12 h-12 text-[#E50914] mx-auto mb-4" />
        <p className="text-gray-400 mb-4">{error}</p>
        <Link to="/festival" className="btn-red px-6 py-2 rounded font-semibold">Back to festival</Link>
      </div>
    </div>
  );
  if (!film) return <div className="min-h-screen bg-[#0A0A0A]"><Navbar /></div>;

  const us = film.user_status || {};
  const token = (typeof localStorage !== "undefined" && localStorage.getItem("cc_token")) || "";
  // API_BASE already ends in "/api" (see lib/api.js), so strip it before adding "/api/files/"
  // to avoid the double "/api/api/" path. Matches the pattern in Watch.jsx and LivePlayer.jsx.
  const origin = API_BASE.replace(/\/api$/, "");
  const streamUrl = film.film_path && us.has_ticket ? `${origin}/api/files/${film.film_path}?auth=${encodeURIComponent(token)}` : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      {/* Player modal */}
      {showPlayer && streamUrl && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center px-4" onClick={() => setShowPlayer(false)}>
          <button onClick={() => setShowPlayer(false)} className="absolute top-4 right-4 text-white/60 hover:text-white text-3xl">✕</button>
          <video
            data-testid="festival-film-video"
            src={streamUrl}
            controls
            autoPlay
            crossOrigin={film.subtitles_url ? "anonymous" : undefined}
            className="w-full max-w-5xl max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
            onError={() => toast.error("Stream unavailable")}
          >
            {film.subtitles_url && (
              <track
                kind="subtitles"
                src={`${film.subtitles_url}${film.subtitles_url.includes("?") ? "&" : "?"}auth=${encodeURIComponent(token)}`}
                srcLang={film.language || "en"}
                label={(film.language || "EN").toUpperCase()}
                default
                data-testid="festival-film-subtitles"
              />
            )}
          </video>
        </div>
      )}

      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="relative px-6 lg:px-12 py-12">
          <div className="max-w-[1300px] mx-auto grid lg:grid-cols-[320px_1fr] gap-10">
            <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#141414] border border-white/5">
              {film.poster_url ? (
                <img src={film.poster_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-600"><Film className="w-12 h-12" /></div>
              )}
            </div>
            <div className="flex flex-col">
              <button onClick={() => nav("/festival")} className="text-gray-500 hover:text-white text-xs flex items-center gap-1 mb-4 self-start">
                <ArrowLeft className="w-3 h-3" /> Festival
              </button>
              <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-amber-400">
                <Globe className="w-3.5 h-3.5" /> {film.region || "—"} · {film.country || ""} · {film.runtime_min} min · {film.language?.toUpperCase()}
              </div>
              <h1 data-testid="film-title" className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-3">{displayTitle}</h1>
              {(copy || copyBusy) && (
                <div className="mb-4 inline-flex items-center gap-2 text-[11px]" data-testid="film-translate-chip">
                  <Languages className="w-3.5 h-3.5 text-blue-300" />
                  {copyBusy && !copy ? (
                    <span className="text-gray-400">Translating to your language…</span>
                  ) : (
                    <>
                      <span className="text-blue-200">
                        {showOriginal
                          ? `Showing original (${(copy?.source_lang || "en").toUpperCase()})`
                          : `Auto-translated to ${(copy?.target || "").toUpperCase()}${copy?.cached ? " · cached" : ""}`}
                      </span>
                      <button
                        onClick={() => setShowOriginal((v) => !v)}
                        data-testid="film-translate-toggle"
                        className="text-blue-300 hover:text-white underline underline-offset-2"
                      >
                        {showOriginal ? "Show translation" : "Show original"}
                      </button>
                    </>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="font-bold">{(film.score || 0).toFixed(2)}</span>
                </div>
                <span className="text-gray-500 text-sm">· {film.total_votes || 0} votes · {film.view_count || 0} views</span>
                <span className="text-gray-500">·</span>
                <span className="text-gray-400 text-sm">by{" "}
                  {film.filmmaker?.user_id ? (
                    <Link data-testid="filmmaker-link" to={`/filmmaker/${film.filmmaker.user_id}`} className="text-white hover:text-amber-400 underline-offset-4 hover:underline">
                      {film.filmmaker?.name || "Filmmaker"}
                    </Link>
                  ) : (
                    <span className="text-white">{film.filmmaker?.name || "Filmmaker"}</span>
                  )}
                </span>
              </div>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-6 max-w-2xl" data-testid="film-synopsis">{displaySynopsis}</p>

              {/* Trailer (always public) */}
              {film.youtube_trailer_id && (
                <div className="mb-6 max-w-3xl">
                  <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Trailer (free preview)</div>
                  <div className="aspect-video rounded-lg overflow-hidden border border-white/5">
                    <iframe
                      src={`https://www.youtube.com/embed/${film.youtube_trailer_id}`}
                      className="w-full h-full"
                      title="trailer"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}

              {/* CTA: buy ticket OR watch */}
              <div className="flex flex-wrap gap-3 items-center">
                {us.has_ticket ? (
                  film.film_path ? (
                    <button
                      data-testid="watch-film-btn"
                      onClick={() => setShowPlayer(true)}
                      className="btn-red px-8 py-3 rounded font-semibold flex items-center gap-2"
                    >
                      <Play className="w-4 h-4 fill-current" /> Watch the film
                    </button>
                  ) : (
                    <span className="text-amber-400 text-sm">Filmmaker hasn't uploaded the full film yet — trailer only.</span>
                  )
                ) : (
                  <button
                    data-testid="buy-festival-ticket-btn"
                    onClick={buy}
                    disabled={buying}
                    className="btn-red px-8 py-3 rounded font-semibold flex items-center gap-2 disabled:opacity-50"
                  >
                    {buying ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening Stripe…</> : <><Ticket className="w-4 h-4" /> Buy ticket & vote</>}
                  </button>
                )}
                {us.is_filmmaker && (
                  <span className="text-xs text-amber-400 uppercase tracking-widest">Your film</span>
                )}
                {user && !us.is_filmmaker && (
                  <button
                    data-testid="report-btn"
                    onClick={() => setReportOpen(true)}
                    className="text-gray-500 hover:text-red-400 text-xs flex items-center gap-1.5 ml-auto"
                    title="Report this film"
                  >
                    <Flag className="w-3.5 h-3.5" /> Report
                  </button>
                )}
              </div>

              {/* Voting */}
              {us.has_ticket && !us.is_filmmaker && (
                <div data-testid="vote-section" className="mt-8 bg-black/40 border border-amber-500/30 rounded-lg p-5 max-w-md">
                  <div className="text-xs uppercase tracking-widest text-amber-400 mb-2 font-bold">Audience Vote</div>
                  <div className="text-sm text-gray-400 mb-3">
                    {us.has_voted ? `You rated this ${us.user_rating}/5. Tap to change.` : "Rate 1-5 stars after watching."}
                  </div>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        data-testid={`vote-star-${n}`}
                        disabled={voting}
                        onClick={() => castVote(n)}
                        className={`p-2 rounded transition-all hover:scale-110 ${
                          us.user_rating && us.user_rating >= n
                            ? "text-amber-400" : "text-gray-600 hover:text-amber-300"
                        }`}
                        aria-label={`${n} stars`}
                      >
                        <Star className={`w-7 h-7 ${us.user_rating && us.user_rating >= n ? "fill-amber-400" : ""}`} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(film.genres || []).length > 0 && (
                <div className="mt-6 flex flex-wrap gap-1.5">
                  {film.genres.map((g) => (
                    <span key={g} className="text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 rounded px-2 py-1 text-gray-400">{g}</span>
                  ))}
                </div>
              )}

              {(film.collaborators || []).length > 0 && (
                <div data-testid="credits-section" className="mt-8 max-w-2xl">
                  <div className="text-xs uppercase tracking-widest text-gray-500 mb-3 font-bold">Credits</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {film.collaborators.map((c) => (
                      <Link
                        key={c.user_id}
                        data-testid={`credit-${c.user_id}`}
                        to={`/filmmaker/${c.user_id}`}
                        className="flex items-center gap-2.5 bg-[#141414] border border-white/5 hover:border-amber-500/40 rounded p-2.5 transition-colors group"
                      >
                        {c.picture ? (
                          <img src={c.picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-700/40 to-[#E50914]/40 flex items-center justify-center text-[10px] font-black flex-shrink-0">
                            {(c.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold truncate group-hover:text-amber-300 transition-colors">{c.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-gray-500">{c.role}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {reportOpen && (
        <div data-testid="report-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4" onClick={() => !reporting && setReportOpen(false)}>
          <div className="relative w-full max-w-md bg-gradient-to-br from-[#1a0a0a] to-[#0a0a0a] border border-red-500/40 rounded-xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => !reporting && setReportOpen(false)} className="absolute top-3 right-3 text-gray-400 hover:text-white" aria-label="Close">✕</button>
            <div className="flex items-center gap-2 mb-2">
              <Flag className="w-5 h-5 text-red-400" />
              <h3 className="text-xl font-black">Report this film</h3>
            </div>
            <p className="text-sm text-gray-400 mb-4">Help us keep the festival safe. Reports are anonymous and reviewed by moderators.</p>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">Reason</label>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { v: "inappropriate", l: "Inappropriate" },
                { v: "copyright", l: "Copyright" },
                { v: "spam", l: "Spam" },
                { v: "other", l: "Other" },
              ].map((o) => (
                <button
                  key={o.v}
                  data-testid={`report-reason-${o.v}`}
                  onClick={() => setReportReason(o.v)}
                  className={`px-3 py-2 rounded text-sm border transition-all ${
                    reportReason === o.v ? "bg-red-500/15 border-red-500/60 text-white" : "border-white/10 text-gray-400 hover:border-white/30"
                  }`}
                >{o.l}</button>
              ))}
            </div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Details (optional)</label>
            <textarea
              data-testid="report-note"
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="What should the moderators know?"
              className="w-full bg-black/60 border border-white/15 focus:border-red-500 outline-none rounded px-3 py-2 text-sm mb-4 resize-none"
              disabled={reporting}
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setReportOpen(false)} disabled={reporting} className="btn-glass px-4 py-2 rounded text-sm">Cancel</button>
              <button data-testid="report-submit" onClick={submitReport} disabled={reporting} className="btn-red px-5 py-2 rounded text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                {reporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />} Submit report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Q&A floating toggle */}
      {user && (
        <button
          data-testid="festival-chat-toggle"
          onClick={() => {
            if (!canJoinChat) {
              toast.info("Buy a ticket to join the live Q&A");
              return;
            }
            setChatOpen((v) => !v);
          }}
          className={`fixed bottom-6 right-6 z-40 rounded-full px-5 py-3 font-semibold text-sm flex items-center gap-2 shadow-lg transition-all ${
            canJoinChat
              ? "bg-gradient-to-br from-amber-500 to-[#E50914] text-white hover:brightness-110"
              : "bg-black/70 border border-white/15 text-gray-400 hover:text-white"
          }`}
          aria-label="Live Q&A chat"
        >
          <MessageSquare className="w-4 h-4" />
          <span>{chatOpen ? "Close chat" : "Live Q&A"}</span>
          {chatViewers > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-black/30 rounded-full px-2 py-0.5">
              <Users className="w-3 h-3" />{chatViewers}
            </span>
          )}
          {!chatOpen && chatUnread > 0 && (
            <span className="bg-white text-[#E50914] text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">{chatUnread}</span>
          )}
        </button>
      )}

      {/* Q&A drawer */}
      {chatOpen && (
        <aside
          data-testid="festival-chat-drawer"
          className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[400px] bg-gradient-to-b from-[#14080a] to-black/95 backdrop-blur-xl border-l border-amber-500/20 flex flex-col shadow-2xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-1.5 rounded bg-gradient-to-br from-amber-500 to-[#E50914]">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-sm truncate">Live Q&A · {film.title}</h3>
                <div className="text-[10px] uppercase tracking-widest text-gray-400 flex items-center gap-2">
                  <span className="flex items-center gap-1"><Users className="w-2.5 h-2.5 text-amber-400" />{chatViewers}</span>
                  {chatStatus === "connecting" && <span className="text-amber-400">· connecting</span>}
                  {chatStatus === "live" && <span className="text-emerald-400">· live</span>}
                  {chatStatus === "denied" && <span className="text-red-400">· ticket required</span>}
                  {chatStatus === "error" && <span className="text-red-400">· error</span>}
                </div>
              </div>
            </div>
            <button data-testid="festival-chat-close" onClick={() => setChatOpen(false)} aria-label="Close chat" className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {chatRole && (
            <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/5">
              You're joining as <span className="text-amber-400 font-bold">{roleBadge(chatRole)?.label || chatRole}</span>
            </div>
          )}

          <div ref={chatBodyRef} data-testid="festival-chat-body" className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {chatMessages.length === 0 && chatStatus === "live" && (
              <div className="text-xs text-gray-500 text-center mt-8 px-4">
                No messages yet. Ask the filmmaker a question — they often join the live Q&A.
              </div>
            )}
            {chatStatus === "connecting" && (
              <div className="text-xs text-gray-500 text-center mt-8">Connecting to the Q&A room…</div>
            )}
            {chatStatus === "denied" && (
              <div className="text-xs text-gray-500 text-center mt-8 px-4">
                You need a ticket to join the Q&A. Buy a ticket to unlock the film and this chat.
              </div>
            )}
            {chatMessages.map((m, i) => {
              const badge = roleBadge(m.role);
              const Icon = badge?.icon;
              return (
                <div key={i} data-testid={`chat-msg-${i}`} className={`rounded-lg px-3 py-2 ${badge ? "bg-black/50 border border-amber-500/20" : "bg-white/[0.03]"}`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-sm font-bold ${badge ? "text-amber-300" : "text-white"}`}>{m.name}</span>
                    {badge && (
                      <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold rounded border px-1.5 py-0.5 ${badge.cls}`}>
                        {Icon && <Icon className="w-2.5 h-2.5" />}
                        {badge.label}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-200 whitespace-pre-wrap break-words">{m.text}</div>
                </div>
              );
            })}
          </div>

          <div className="border-t border-white/10 px-3 py-3 flex items-center gap-2">
            <input
              data-testid="festival-chat-input"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value.slice(0, 500))}
              onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
              placeholder={chatStatus === "live" ? "Ask a question…" : "Waiting to connect…"}
              disabled={chatStatus !== "live"}
              maxLength={500}
              className="flex-1 bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm disabled:opacity-50"
            />
            <button
              data-testid="festival-chat-send"
              onClick={sendChat}
              disabled={chatStatus !== "live" || !chatInput.trim()}
              className="bg-gradient-to-br from-amber-500 to-[#E50914] text-white px-3 py-2 rounded disabled:opacity-40"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </aside>
      )}
    </div>
  );
}
